import { type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CircleCheck, CircleDashed } from 'lucide-react';
import { ChartFrame } from '../../../components/chart/chart-frame';
import { Button } from '../../../components/ui/button';
import { errorMessage } from '../../../lib/error-message';
import { ganttApi } from '../../gantt/api';
import { reportApi, reportKeys } from '../api';

/**
 * Trạng thái cổng pha — spec 16.3.
 *
 * Dạng danh sách, không phải hình vẽ: một cổng pha có bốn thứ cần đọc cùng lúc
 * (đã qua chưa, hạn baseline, trễ mấy ngày, còn mấy điều kiện chưa xong) và một
 * trục toạ độ không chở nổi bốn thứ đó.
 *
 * **Điều kiện qua cổng** đọc từ đồ thị phụ thuộc, không phải một trường nhập
 * tay — nên nó không thể lệch với sơ đồ Gantt.
 *
 * ## Nút "Qua cổng pha" chuyển về đây — APM-GANTT-k
 *
 * Trước đây nó là cửa vào **duy nhất**, và nó nằm ở thanh chi tiết dưới đáy
 * Gantt: phải bấm trúng một hình thoi rồi mới thấy nút. Bỏ thanh ấy đi (biểu đồ
 * cần chiều cao hơn) thì nút phải có nhà mới, và đây là màn hình dành riêng cho
 * cổng pha — nó đã hiện sẵn còn mấy điều kiện chưa xong, tức đúng thông tin cần
 * đọc **trước khi** bấm.
 *
 * Nút chỉ hiện khi đủ hai điều: chưa qua, và mọi điều kiện đã xong. Cổng pha là
 * lời tuyên bố "pha này khép lại được rồi" — mời bấm khi còn việc dở là mời
 * tuyên bố một điều chưa đúng. Server vẫn có luật của nó; chỗ này chỉ thôi mời.
 */
export function PhaseGateList({
  projectId,
  asOf,
  canPass = false,
}: {
  projectId: string;
  asOf?: string | undefined;
  /** `task:update` ở dự án này. Bỏ trống = chỉ đọc. */
  canPass?: boolean;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const report = useQuery({
    queryKey: reportKeys.phaseGates(projectId, asOf ?? ''),
    queryFn: () => reportApi.phaseGates(projectId, asOf),
  });

  const pass = useMutation({
    mutationFn: (taskId: string) => ganttApi.passGate(taskId),
    onSuccess: () => toast.success(t('gantt.gatePassed')),
    onError: (error) => toast.error(errorMessage(error, t)),
    onSettled: () => {
      // Cổng pha là một task: qua cổng đổi cả báo cáo này, cả Gantt, cả board.
      void queryClient.invalidateQueries({ queryKey: ['reports', projectId], exact: false });
      void queryClient.invalidateQueries({ queryKey: ['gantt', projectId], exact: false });
      void queryClient.invalidateQueries({ queryKey: ['board', projectId], exact: false });
    },
  });

  const gates = report.data?.gates ?? [];
  const passed = gates.filter((gate) => gate.passed).length;

  return (
    <ChartFrame
      title={t('reports.gatesTitle')}
      help="gates"
      subtitle={t('reports.gatesSubtitle', { passed, total: gates.length })}
      asOf={asOf ? new Date(asOf) : undefined}
      loading={report.isPending}
      error={report.isError ? errorMessage(report.error, t) : null}
      onRetry={() => void report.refetch()}
      empty={
        report.data && gates.length === 0
          ? { title: t('reports.gatesEmpty'), description: t('reports.gatesEmptyHelp') }
          : null
      }
    >
      <ul className="flex flex-col divide-y divide-border">
        {gates.map((gate) => (
          <li key={gate.taskId} className="flex flex-wrap items-center gap-2 py-1.5 text-xs">
            {gate.passed ? (
              <CircleCheck aria-hidden className="size-4 text-rag-green" />
            ) : (
              <CircleDashed aria-hidden className="size-4 text-rag-unknown" />
            )}
            <span className="font-mono text-muted-foreground">{gate.taskKey}</span>
            <span className="truncate font-medium">{gate.title}</span>

            <span className="text-muted-foreground">
              {gate.passed ? t('reports.gatePassed') : t('reports.gateWaiting')}
            </span>

            {gate.blockerCount > 0 && (
              <span
                className={
                  gate.blockersDone === gate.blockerCount ? 'text-rag-green' : 'text-rag-amber'
                }
              >
                {t('reports.gateBlockers', {
                  done: gate.blockersDone,
                  total: gate.blockerCount,
                })}
              </span>
            )}

            {gate.slippageDays !== null && gate.slippageDays !== 0 && (
              <span
                className={`ml-auto shrink-0 ${gate.slippageDays > 0 ? 'text-rag-red' : 'text-rag-green'}`}
              >
                {gate.slippageDays > 0
                  ? t('reports.gateLate', { days: gate.slippageDays })
                  : t('reports.gateEarly', { days: -gate.slippageDays })}
              </span>
            )}

            {canPass && !gate.passed && gate.blockersDone === gate.blockerCount && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="ml-auto shrink-0"
                disabled={pass.isPending}
                onClick={() => pass.mutate(gate.taskId)}
              >
                {t('gantt.passGate')}
              </Button>
            )}
          </li>
        ))}
      </ul>
    </ChartFrame>
  );
}
